/**
 * The onboarding-v2 arc's server actions (RO-2-07, docs/rollover/inventory/
 * actions.md "Domain: welcome" — the 11 `app/welcome/_v2/actions.ts` rows),
 * converted to contracted Hono endpoints. Source:
 * `excellent/app/welcome/_v2/actions.ts` (ONBOARDING-V2-PRD §9).
 *
 * The five-station arc persists each station transition, its two decisions
 * (identity confirm, plan compose), the telemetry consent, and the first-value
 * apply/read-back. Every one of these goes through
 * the SAME durable seam the source action used — `patchOnboardingState`,
 * `writeDomainSetting`, `upsertPlayAction` — never a parallel store. See
 * `welcome.authz.ts` for the capability classification of each mutating route.
 *
 * ── Two shape changes the HTTP boundary forces, and nothing else ────────────
 *
 * 1. `finishV2Onboarding` ended in `redirect("/")`. An endpoint cannot drive a
 *    Next navigation, so the target becomes DATA (`redirectTo: "/"`) and the
 *    caller navigates. The PRD §9 decision it encodes (land on HOME, not the
 *    `/agents` detour) is preserved as the value, not deleted.
 * 2. Actions returning `void` return `{ ok: true, ... }` here, because an HTTP
 *    caller has no other way to learn the write landed. Where the source
 *    action deliberately swallowed a failure (`applyFirstValueAction`) the
 *    swallowing is preserved and reported as `ok: false` DATA in a 200 — not
 *    promoted to a transport error, which would change the arc's behavior from
 *    "best-effort" to "blocking".
 *
 * Behavior otherwise preserved verbatim, including the deliberate no-ops: a
 * non-v2 station id, a blank domain, and a blank workspace name are all
 * silently ignored by the source and are silently ignored here (reported as
 * `persisted: false` / `domain: null` / `workspaceId: null`), NOT turned into
 * a 400. Onboarding is "detect, don't ask" — a no-op must not become a
 * question.
 */

import { z } from "zod";
import type { Operation } from "../types";
// The one deliberate cross-contract import in this directory. `onboardingStep`
// is a PERMANENCE INVARIANT (`excellent/src/onboarding/steps.ts:9-19`): the
// frozen `onboarding_state` DDL default is the LEGACY id `'info'` and the
// additive-only migration rail can never change a column default, so the enum
// must stay permissive of legacy ids forever. Re-declaring those 21 members
// here would be exactly the drift that comment forbids, so this imports the
// canonical enum instead of forking it.
import { onboardingStep } from "./onboarding";

const envName = z.enum(["demo", "real"]);
const envQuery = z.object({ env: envName.optional() });
type EnvQuery = z.infer<typeof envQuery>;

// ---------------------------------------------------------------------------
// persistStation (actions.ts:50) — persist a station transition.
//
// The body accepts the WIDE step enum, not the five v2 station ids, because
// the source guards with `isV2Station` and silently RETURNS on a non-v2 id.
// Narrowing the enum here would convert that documented no-op into a 400.

export const welcomeStationBody = z.object({ station: onboardingStep });
export type WelcomeStationBody = z.infer<typeof welcomeStationBody>;

export const welcomeStationResponse = z.object({
  ok: z.literal(true),
  /** false when the id is not a v2 station — the source's silent no-op. */
  persisted: z.boolean(),
});

export const welcomePersistStation: Operation<
  void,
  void,
  WelcomeStationBody,
  z.infer<typeof welcomeStationResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/station",
  body: welcomeStationBody,
  response: welcomeStationResponse,
};

// ---------------------------------------------------------------------------
// confirmIdentityDomain (actions.ts:64) — dual-write the domain, fan out the
// section-fillers, stamp the enrichment kick-off. A blank/invalid domain is a
// no-op (the personal path, or a company with no site).

export const welcomeIdentityDomainBody = z.object({
  domain: z.string().describe("Raw domain — normalized server-side; blank is a no-op."),
});
export type WelcomeIdentityDomainBody = z.infer<typeof welcomeIdentityDomainBody>;

export const welcomeIdentityDomainResponse = z.object({
  ok: z.literal(true),
  /** The normalized host, or null when the input did not normalize (no-op). */
  domain: z.string().nullable(),
  /** How many section-filler workers were dispatched (0 on the no-op path). */
  spawned: z.number(),
  /** Why none spawned, when the spawner declined. */
  reason: z.string().nullable(),
});

export const welcomeConfirmIdentityDomain: Operation<
  void,
  void,
  WelcomeIdentityDomainBody,
  z.infer<typeof welcomeIdentityDomainResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/identity/domain",
  body: welcomeIdentityDomainBody,
  response: welcomeIdentityDomainResponse,
};

// ---------------------------------------------------------------------------
// nameWorkspace (actions.ts:84) — rename the bootstrap workspace + stash the
// tagline on the onboarding state. Blank name is a no-op so a fast identity
// confirm never wipes a prior name.

export const welcomeNameWorkspaceBody = z.object({
  name: z.string(),
  tagline: z.string(),
});
export type WelcomeNameWorkspaceBody = z.infer<typeof welcomeNameWorkspaceBody>;

export const welcomeNameWorkspaceResponse = z.object({
  ok: z.literal(true),
  /** The workspace that was renamed, or null on the blank-name no-op. */
  workspaceId: z.number().nullable(),
});

export const welcomeNameWorkspace: Operation<
  void,
  void,
  WelcomeNameWorkspaceBody,
  z.infer<typeof welcomeNameWorkspaceResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/identity/workspace",
  body: welcomeNameWorkspaceBody,
  response: welcomeNameWorkspaceResponse,
};

// ---------------------------------------------------------------------------
// composePlan (actions.ts:110) — persist the accepted DomainSuggestion[].
// Mirrors `DomainSuggestion` / `ConnectApp` (`excellent/src/onboarding/
// suggest.ts:52,59`). Composing connects NOTHING — every integration keeps its
// own consent moment (trust-matrix rule, ONBOARDING-V2-PRD §5).

export const connectAppSchema = z.object({
  /** a REAL PROVIDER_AUTH slug (provider-auth.ts) — never invented */
  slug: z.string(),
  label: z.string(),
});

export const domainSuggestionSchema = z.object({
  domain: z.string(),
  because: z.string(),
  signal: z.string().nullable(),
  track: z.array(z.string()),
  connect: z.array(connectAppSchema),
  payoff: z.string(),
  /** rank tier: 3 = detected tool, 2 = page presence, 1 = generic */
  tier: z.union([z.literal(1), z.literal(2), z.literal(3)]),
});
export type DomainSuggestionDto = z.infer<typeof domainSuggestionSchema>;

export const welcomeComposePlanBody = z.object({
  suggestions: z.array(domainSuggestionSchema),
});
export type WelcomeComposePlanBody = z.infer<typeof welcomeComposePlanBody>;

export const welcomeComposePlanResponse = z.object({ ok: z.literal(true) });

export const welcomeComposePlan: Operation<
  void,
  void,
  WelcomeComposePlanBody,
  z.infer<typeof welcomeComposePlanResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/plan/compose",
  body: welcomeComposePlanBody,
  response: welcomeComposePlanResponse,
};

// ---------------------------------------------------------------------------
// declinePlan (actions.ts:125) — record `accepted:false` so the home can tell
// a DECLINED plan from a never-reached one.

export const welcomeDeclinePlanResponse = z.object({ ok: z.literal(true) });

export const welcomeDeclinePlan: Operation<
  void,
  void,
  void,
  z.infer<typeof welcomeDeclinePlanResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/plan/decline",
  response: welcomeDeclinePlanResponse,
};

// ---------------------------------------------------------------------------
// setPlanTelemetryOptIn (actions.ts:136) — the consent toggle (default off).
// Patches `telemetry_opt_in` + stamps/clears `telemetry_opt_in_at`, which is
// what makes egress from-consent-only.

export const welcomeTelemetryOptInBody = z.object({ optIn: z.boolean() });
export type WelcomeTelemetryOptInBody = z.infer<typeof welcomeTelemetryOptInBody>;

export const welcomeTelemetryOptInResponse = z.object({
  ok: z.literal(true),
  optIn: z.boolean(),
});

export const welcomeSetPlanTelemetryOptIn: Operation<
  void,
  void,
  WelcomeTelemetryOptInBody,
  z.infer<typeof welcomeTelemetryOptInResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/plan/telemetry",
  body: welcomeTelemetryOptInBody,
  response: welcomeTelemetryOptInResponse,
};

// ---------------------------------------------------------------------------
// applyFirstValueAction (actions.ts:179) — a durable Play handoff. Idempotent
// on the action_key, never reopens a card the user dismissed later, and
// best-effort: a persist failure returns `ok:false` DATA rather than throwing,
// exactly as the source swallowed it.

export const firstValueForecastSchema = z.object({
  usdPerMonth: z.number().nullable(),
  pctOfWeek: z.number().nullable(),
  hoursPerWeek: z.number().nullable(),
  /** the exact math string the card showed (audit of what was promised) */
  math: z.string(),
});

export const welcomeApplyFirstValueBody = z.object({
  id: z.string(),
  title: z.string(),
  group: z.string(),
  forecast: firstValueForecastSchema,
  apply: z.object({ receipt: z.string() }),
  executor: z.string(),
});
export type WelcomeApplyFirstValueBody = z.infer<typeof welcomeApplyFirstValueBody>;

export const welcomeApplyFirstValueResponse = z.object({
  ok: z.boolean(),
  created: z.boolean(),
});

export const welcomeApplyFirstValueAction: Operation<
  void,
  void,
  WelcomeApplyFirstValueBody,
  z.infer<typeof welcomeApplyFirstValueResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/first-value/apply",
  body: welcomeApplyFirstValueBody,
  response: welcomeApplyFirstValueResponse,
};

// ---------------------------------------------------------------------------
// listAppliedFirstValueActions (actions.ts:231) — READ. The applied card ids,
// read back from the durable `play_actions` rows so the tracking strip
// survives a refresh/resume. A genuine read: the only statement in its
// transitive closure is one SELECT (see welcome.authz.ts for the audit).

export const welcomeAppliedFirstValueResponse = z.object({
  /** bare card ids, the `first-value:` prefix stripped */
  applied: z.array(z.string()),
});

export const welcomeListAppliedFirstValueActions: Operation<
  void,
  void,
  void,
  z.infer<typeof welcomeAppliedFirstValueResponse>
> = {
  method: "GET",
  path: () => "/api/welcome/first-value/applied",
  response: welcomeAppliedFirstValueResponse,
};

// ---------------------------------------------------------------------------
// finishV2Onboarding (actions.ts:281) — "Enter Excellent". Sets `completed_at`,
// runs the same best-effort side effects (lane graph, self-entities), fires the
// first-run audit. `redirectTo` carries the PRD §9 decision (HOME, not the
// /agents detour) as data, since an endpoint cannot drive the navigation.

export const welcomeFinishResponse = z.object({
  ok: z.literal(true),
  /** true when this call started the first-run audit */
  firstRunAuditStarted: z.boolean(),
  /** PRD §9 Wave B: the composed payoff is HOME, never the /agents detour. */
  redirectTo: z.literal("/"),
});

export const welcomeFinishOnboarding: Operation<
  void,
  EnvQuery,
  void,
  z.infer<typeof welcomeFinishResponse>
> = {
  method: "POST",
  path: () => "/api/welcome/finish",
  query: envQuery,
  response: welcomeFinishResponse,
};

export const welcome = {
  persistStation: welcomePersistStation,
  confirmIdentityDomain: welcomeConfirmIdentityDomain,
  nameWorkspace: welcomeNameWorkspace,
  composePlan: welcomeComposePlan,
  declinePlan: welcomeDeclinePlan,
  setPlanTelemetryOptIn: welcomeSetPlanTelemetryOptIn,
  applyFirstValueAction: welcomeApplyFirstValueAction,
  listAppliedFirstValueActions: welcomeListAppliedFirstValueActions,
  finishOnboarding: welcomeFinishOnboarding,
};
